/**
 * WelcomeStep - First step of the onboarding flow
 *
 * Displays a welcome message with the app's value proposition.
 * Uses the existing glassmorphic design system for visual consistency.
 */

import React from 'react';
import { View, Text, StyleSheet } from 'react-native';
import { useTranslation } from '@dramadetector/shared';

interface WelcomeStepProps {
  /** User's display name (optional, for personalization) */
  displayName?: string;
}

export const WelcomeStep: React.FC<WelcomeStepProps> = ({ displayName }) => {
  const { t } = useTranslation('common');

  const greeting = displayName
    ? t('welcome_greeting_name', { name: displayName, defaultValue: 'Welcome, {{name}}!' })
    : t('welcome_greeting', { defaultValue: 'Welcome to DramaDetector!' });

  return (
    <View style={styles.container}>
      <Text style={styles.emoji}>{'🎭'}</Text>
      <Text style={styles.title}>{greeting}</Text>
      <Text style={styles.subtitle}>
        {t('welcome_subtitle', { defaultValue: 'Your message helper before you send' })}
      </Text>
      <View style={styles.valueProps}>
        <View style={styles.valuePropItem}>
          <Text style={styles.valuePropIcon}>{'🔍'}</Text>
          <Text style={styles.valuePropText}>
            {t('welcome_value_detect', { defaultValue: 'See how your message lands before you send' })}
          </Text>
        </View>
        <View style={styles.valuePropItem}>
          <Text style={styles.valuePropIcon}>{'💡'}</Text>
          <Text style={styles.valuePropText}>
            {t('welcome_value_insights', { defaultValue: 'Get actionable advice on every conversation' })}
          </Text>
        </View>
        <View style={styles.valuePropItem}>
          <Text style={styles.valuePropIcon}>{'✨'}</Text>
          <Text style={styles.valuePropText}>
            {t('welcome_value_rewrite', { defaultValue: 'Rewrite messages with healthier alternatives' })}
          </Text>
        </View>
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 16,
  },
  emoji: {
    fontSize: 64,
    marginBottom: 16,
  },
  title: {
    fontSize: 28,
    fontWeight: '700',
    color: '#1a1a2e',
    textAlign: 'center',
    marginBottom: 8,
    fontFamily: 'PlusJakartaSans_700Bold',
  },
  subtitle: {
    fontSize: 16,
    color: '#6b7280',
    textAlign: 'center',
    marginBottom: 32,
    fontFamily: 'Inter_400Regular',
  },
  valueProps: {
    width: '100%',
    gap: 16,
  },
  valuePropItem: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingVertical: 8,
  },
  valuePropIcon: {
    fontSize: 24,
    width: 32,
    textAlign: 'center',
  },
  valuePropText: {
    fontSize: 15,
    color: '#374151',
    flex: 1,
    fontFamily: 'Inter_400Regular',
    lineHeight: 22,
  },
});
